import { useRef, useCallback } from 'react';
import { getLogoPath } from '../../utils/brandLogoMapper';

export function useLogoCaching() {
  const logoCache = useRef<Map<string, string>>(new Map());
  const loadingPromises = useRef<Map<string, Promise<string>>>(new Map());

  const fetchBrandLogo = useCallback(async (brandName: string): Promise<string> => {
    const normalizedKey = brandName.trim().toUpperCase().replace(/[''´`'"]/g, '').replace(/\s+/g, ' ');

    // Return from cache if available
    if (logoCache.current.has(normalizedKey)) {
      return logoCache.current.get(normalizedKey)!;
    }

    // If already loading, return existing promise
    if (loadingPromises.current.has(normalizedKey)) {
      return loadingPromises.current.get(normalizedKey)!;
    }

    // Create loading promise
    const loadingPromise = (async () => {
      try {
        const logoPath = await getLogoPath(brandName);

        // If it's already a data URL (from fallback SVG), return it directly
        if (logoPath.startsWith('data:')) {
          logoCache.current.set(normalizedKey, logoPath);
          return logoPath;
        }

        const absoluteUrl = logoPath.startsWith('http')
          ? logoPath
          : `${window.location.origin}${logoPath}`;

        const response = await fetch(absoluteUrl);
        const blob = await response.blob();
        const buffer = await blob.arrayBuffer();
        const bytes = new Uint8Array(buffer);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const dataUrl = `data:${blob.type};base64,${btoa(binary)}`;
        logoCache.current.set(normalizedKey, dataUrl);
        return dataUrl;
      } catch (error) {
        console.error('Error fetching logo for', brandName, ':', error);
        logoCache.current.set(normalizedKey, '');
        return '';
      } finally {
        loadingPromises.current.delete(normalizedKey);
      }
    })();

    loadingPromises.current.set(normalizedKey, loadingPromise);
    return loadingPromise;
  }, []);

  return { fetchBrandLogo, logoCache };
}
